import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { FaxService } from 'app/fm/services/fax-service';
import { AuthService } from 'app/um/services/auth-service';
import { CaseService as CMCaseService } from 'app/cm/services/case-service';
import { AppService } from 'app/shared/services/app-service';
import { CaseService as AGCaseService } from 'app/ag/services/case-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { task } from 'app/shared/lib/decorators';
import { forceNull } from 'app/shared/lib/utilities';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as FM from 'app/fm/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as AG from 'app/ag/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SaveFaxQueue extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView })
    protected faxQueue: FM.FaxQueue = {};

    protected allFaxQueues: FM.FaxQueue[] = [];

    @observable
    protected selectedActions: FM.FaxQueueAction[] = [];
    protected selectedAuthClass: UM.AuthClass | null = null;
    protected selectedCaseType: AG.CaseType | null = null;
    protected selectedRequestMethod: AG.RequestMethod | null = null;
    protected selectedReferralSource: CM.ReferralSource | null = null;

    protected queueActions: ReadonlyArray<FM.FaxQueueAction> = [];

    protected authClasses: ReadonlyArray<UM.AuthClass> = [];
    protected caseTypes: ReadonlyArray<AG.CaseType> = [];
    protected requestMethods: ReadonlyArray<AG.RequestMethod> = [];
    protected referralSources: ReadonlyArray<CM.ReferralSource> = [];

    protected displayAuthClassesDropdown = false;
    protected displayCaseTypesDropdown = false;
    protected displayRequestMethodsDropdown = false;
    protected displayReferralSourcesDropdown = false;
    protected hasUM = false;
    protected hasAG = false;
    protected hasCM = false;

    private referralSourceSorting = new Sorting();

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly appService: AppService,
        private readonly faxService: FaxService,
        private readonly authService: AuthService,
        private readonly agCaseService: AGCaseService,
        private readonly cmCaseService: CMCaseService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        this.referralSourceSorting.entries = [
            {
                reference: 'sort.level1',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async attached(): Promise<void> {
        // load lookups
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected faxQueueChanged(): void {
        this.loadFaxQueues();
        this.selectedActions = this.faxQueue?.actions ?? [];
        if(this.selectedActions.length) this.toggleParameterDropdowns();
        this.selectedAuthClass = null;
        this.selectedCaseType = null;
        this.selectedRequestMethod = null;
        this.selectedReferralSource = null;

        // only proceed if fax queue parameters are specified
        if(!this.faxQueue.parameters?.length) return;

        // otherwise, find selections based on each queue parameter's argument
        const authClassParameter = this.faxQueue.parameters.find(p => p.description === 'Auth Class');
        if(authClassParameter) {
            const authClassId = authClassParameter.argument;
            this.selectedAuthClass = this.authClasses.find(ac => ac.id == authClassId) || null;
        }

        const caseTypeParameter = this.faxQueue.parameters.find(p => p.description === 'A&G Case Type');
        if(caseTypeParameter) {
            const caseTypeId = caseTypeParameter.argument;
            this.selectedCaseType = this.caseTypes.find(ct => ct.id == caseTypeId) || null;
        }

        const requestMethodParameter = this.faxQueue.parameters.find(p => p.description === 'A&G How Requested') || null;
        if(requestMethodParameter) {
            const requestMethodId = requestMethodParameter.argument;
            this.selectedRequestMethod = this.requestMethods.find(rm => rm.id == requestMethodId) || null;
        }

        const referralSourceParameter = this.faxQueue.parameters.find(p => p.description === 'CM Referred By') || null;
        if(referralSourceParameter) {
            const referralSourceId = referralSourceParameter.argument;
            this.selectedReferralSource = this.referralSources.find(rs => rs.id === referralSourceId) || null;
        }
    }

    @task
    protected toggleParameterDropdowns() {
        this.displayAuthClassesDropdown = this.hasUM && this.selectedActions.some(a => a.parameters?.some(p => p.description == 'Auth Class'));
        if(!this.displayAuthClassesDropdown) this.selectedAuthClass = null;
        this.displayCaseTypesDropdown = this.hasAG && this.selectedActions.some(a => a.parameters?.some(p => p.description == 'A&G Case Type'));
        if(!this.displayCaseTypesDropdown) this.selectedCaseType = null;
        this.displayRequestMethodsDropdown = this.hasAG && this.selectedActions.some(a => a.parameters?.some(p => p.description == 'A&G How Requested'));
        if(!this.displayRequestMethodsDropdown) this.selectedRequestMethod = null;
        this.displayReferralSourcesDropdown = this.hasCM && this.selectedActions.some(a => a.parameters?.some(p => p.description == 'CM Referred By'));
        if(!this.displayReferralSourcesDropdown) this.selectedReferralSource = null;
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected constructSaveObject(): FM.FaxQueue {
        const saveFaxQueue: FM.FaxQueue = {
            id: this.faxQueue.id,
            description: this.faxQueue.description,
            code: forceNull(this.faxQueue.code),
            actions: []
        }

        const parametersWithArguments: FM.FaxQueueParameterWithArgumentModel[] = [];
        // add selected actions
        this.selectedActions.forEach(action => {
            const actionReference = getReference(action);
            if(actionReference) saveFaxQueue.actions!.push(actionReference);

            // add selected arguments to associated action parameters
            action.parameters?.forEach(p => {
                let parameterAlreadyHasArgument = parametersWithArguments.find(pwa => pwa.id === p.id);
                if(parameterAlreadyHasArgument) return;
                switch(p.description) {
                    case 'Auth Class':
                        if(this.selectedAuthClass?.id) parametersWithArguments.push({ id: p.id, argument: this.selectedAuthClass.id });
                        break;
                    case 'A&G Case Type':
                        if(this.selectedCaseType?.id) parametersWithArguments.push({ id: p.id, argument: this.selectedCaseType.id });
                        break;
                    case 'A&G How Requested':
                        if(this.selectedRequestMethod?.id) parametersWithArguments.push({ id: p.id, argument: this.selectedRequestMethod.id });
                        break;
                    case 'CM Referred By':
                        if(this.selectedReferralSource?.id) parametersWithArguments.push({ id: p.id, argument: this.selectedReferralSource.id });
                        break;
                }
            });
        });

        if(parametersWithArguments.length) saveFaxQueue.parameters = parametersWithArguments;

        return saveFaxQueue;
    }

    public reset(): void {
        this.faxQueue = {};
        this.selectedActions = [];
        this.toggleParameterDropdowns();
    }

    public async submit(): Promise<boolean> {
        return this.saveFaxQueue();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadFaxQueues(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = { all: true, includeInactive: true };
        try {
            this.state.beginLoad();
            const allFaxQueuesResponse = await this.faxService.loadFaxQueues(options);
            this.allFaxQueues = allFaxQueuesResponse.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            // check if client has specific modules before we make any other API calls
            const modulesResponse = await this.appService.getApplicationModules();
            const modules = modulesResponse.results;
            this.hasUM = modules.some(m => m.code === 'UM');
            this.hasAG = modules.some(m => m.code === 'AG');
            this.hasCM = modules.some(m => m.code === 'CM');

            const [queueActionsResponse, authClassesResponse, caseTypesResponse, requestMethodsResponse, referralSourcesResponse] = await Promise.all(
                [
                    this.faxService.loadFaxQueueActions({ all: true, sorting: this.referralSourceSorting }, ['parameters']),
                    this.hasUM ? this.authService.loadAuthClasses({ all: true }) : null,
                    this.hasAG ? this.agCaseService.loadCaseTypes({ all: true }) : null,
                    this.hasAG ? this.agCaseService.loadRequestMethods({ all: true }) : null,
                    this.hasCM ? this.cmCaseService.loadReferralSources({ all: true }) : null
                ]
            );

            this.queueActions = queueActionsResponse.results;
            // if client does not have a specific module, deprive them of that module's actions
            if(!this.hasUM) this.queueActions = this.queueActions
                .filter(a => a.parameters?.some(p => p.description != 'Auth Class'));
            if(!this.hasAG) this.queueActions = this.queueActions
                .filter(a => a.parameters?.some(p => p.description != 'A&G Case Type' && p.description != 'A&G How Requested'));
            if(!this.hasCM) this.queueActions = this.queueActions
                .filter(a => a.parameters?.some(p => p.description != 'CM Referred By'));

            this.authClasses = authClassesResponse?.results || [];
            this.caseTypes = caseTypesResponse?.results || [];
            this.requestMethods = requestMethodsResponse?.results || [];
            this.referralSources = referralSourcesResponse?.results || [];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveFaxQueue(): Promise<boolean> {
        if(!this.faxQueue) return false;

        let isSuccessful, duplicateCodeError = false;
        try {
            this.state.beginSave();

            // check if the code is unique before deleting an edited queue's details
            if(this.faxQueue.code?.trim().length) duplicateCodeError = this.allFaxQueues.some(q => q.code === this.faxQueue.code?.trim() && q.id !== this.faxQueue.id);
            if(duplicateCodeError) throw Error;
            if(this.faxQueue.id) await this.faxService.deleteFaxQueueDetails(this.faxQueue.id);

            const response = await this.faxService.saveFaxQueue(this.constructSaveObject());
            isSuccessful = response.isSuccessful;
            return isSuccessful;
        } catch(error) {
            if(error.status?.errors) duplicateCodeError = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
            throw error;
        } finally {
            if(duplicateCodeError) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('fm:faxQueue.code.$'), item: this.i18n.tr('fm:faxQueue.$long') })).show();
            } else {
                this.toaster.standard.saved('fm:faxQueue.$long', isSuccessful);
            }
            this.state.endSave();
        }
    }
}
